import { Trophy, Award, Download, RotateCcw } from 'lucide-react';
import type { UseCollaborativeGameResult } from '../../../hooks/useCollaborativeGame';
import type { GameEvent } from '../../../types/collaborativeGame';

interface LiveResultsViewProps {
  game: UseCollaborativeGameResult;
  onReset: () => void;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function TranscriptEntry({ event }: { event: GameEvent }) {
  const data = event.eventData as Record<string, unknown>;

  const content = () => {
    switch (event.eventType) {
      case 'join':
        return `${event.participantName} joined${(data.isHost as boolean) ? ' (host)' : ''}`;
      case 'session_started':
        return `${event.participantName} started the session`;
      case 'purchase': {
        const note = data.note as string | undefined;
        return (
          <>
            <strong>{event.participantName}</strong> bought <strong>{data.featureName as string}</strong> — ${data.price as number}
            {note && <div className="mt-0.5 text-[var(--text-ter)] italic">"{note}"</div>}
          </>
        );
      }
      case 'remove_purchase':
        return <><strong>{event.participantName}</strong> removed <strong>{data.featureName as string}</strong></>;
      case 'comment':
        return <><strong>{event.participantName}</strong>: <em>{data.text as string}</em></>;
      case 'done':
        return <><strong>{event.participantName}</strong> finished spending</>;
      case 'session_ended':
        return 'Session ended';
      default:
        return event.eventType;
    }
  };

  const isPurchase = event.eventType === 'purchase';
  const isComment = event.eventType === 'comment';

  return (
    <div className={`flex gap-3 py-2 border-b border-[var(--rule)] last:border-0 ${isPurchase ? 'bg-[var(--green-dim)]/50' : isComment ? 'bg-[var(--slate-dim)]/50' : ''}`}>
      <span className="text-[var(--text-ter)] text-xs shrink-0 pt-0.5 font-mono">{formatTime(event.createdAt)}</span>
      <span className="text-sm text-[var(--text-sec)]">{content()}</span>
    </div>
  );
}

function exportTranscript(game: UseCollaborativeGameResult): void {
  const lines: string[] = ['Buy a Feature — Session Transcript', '='.repeat(50), ''];
  for (const event of game.events) {
    const data = event.eventData as Record<string, unknown>;
    const time = new Date(event.createdAt).toLocaleTimeString();
    let line = `[${time}] `;
    switch (event.eventType) {
      case 'join':      line += `${event.participantName} joined${data.isHost ? ' (host)' : ''}`; break;
      case 'purchase':  line += `${event.participantName} bought ${data.featureName} ($${data.price})${data.note ? ` — "${data.note}"` : ''}`; break;
      case 'remove_purchase': line += `${event.participantName} removed ${data.featureName}`; break;
      case 'comment':   line += `${event.participantName}: ${data.text}`; break;
      case 'done':      line += `${event.participantName} finished spending`; break;
      default:          line += event.eventType;
    }
    lines.push(line);
  }
  lines.push('', '='.repeat(50), 'Results', '');
  const sorted = [...game.features].sort((a, b) => b.totalPurchases - a.totalPurchases);
  sorted.forEach((f, i) => {
    lines.push(`${i + 1}. ${f.name} — ${f.totalPurchases} purchase(s), $${f.totalSpent} total`);
  });

  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `buy-a-feature-${new Date().toISOString().split('T')[0]}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export function LiveResultsView({ game, onReset }: LiveResultsViewProps) {
  const rankedFeatures = [...game.features].sort((a, b) => b.totalPurchases - a.totalPurchases);
  const totalSpent = game.participants.reduce((sum, p) => {
    const config = game.session?.config as { budget_per_participant?: number } | undefined;
    const budget = config?.budget_per_participant ?? 0;
    return sum + (budget - p.remainingBudget);
  }, 0);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-[var(--mustard)]" />;
    if (rank === 2) return <Award className="h-5 w-5 text-[var(--text-ter)]" />;
    if (rank === 3) return <Award className="h-5 w-5 text-[var(--mustard)]" />;
    return <span className="text-[var(--text-ter)] font-bold text-sm">{rank}</span>;
  };

  // Purchase notes from events — the conversation layer
  const purchaseNotes = game.events
    .filter(e => e.eventType === 'purchase')
    .map(e => ({ ...(e.eventData as Record<string, unknown>), by: e.participantName, at: e.createdAt }))
    .filter(e => e.note);

  return (
    <div className="flex gap-6 max-w-6xl mx-auto">
      {/* Results column */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[var(--text-pri)]">Results</h2>
          <div className="flex gap-2">
            <button
              onClick={() => exportTranscript(game)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
            >
              <Download className="h-4 w-4" />
              Export transcript
            </button>
            <button
              onClick={onReset}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              New session
            </button>
          </div>
        </div>

        <div className="space-y-2 mb-8">
          {rankedFeatures.map((feature, index) => {
            const rank = index + 1;
            const maxPurchases = rankedFeatures[0]?.totalPurchases || 1;
            return (
              <div key={feature.id} className="flex items-center gap-3 p-3 bg-[var(--bg-raised)] border border-[var(--rule)]">
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  {getRankIcon(rank)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="font-semibold text-[var(--text-pri)] truncate">{feature.name}</span>
                    <span className="text-sm text-[var(--text-ter)] shrink-0">
                      {feature.totalPurchases} purchase{feature.totalPurchases !== 1 ? 's' : ''} · ${feature.totalSpent}
                    </span>
                  </div>
                  <div className="w-full bg-[var(--hover-tint)] rounded-full h-1.5">
                    <div
                      className="bg-[var(--slate)] h-1.5 rounded-full transition-all"
                      style={{ width: `${(feature.totalPurchases / maxPurchases) * 100}%` }}
                    />
                  </div>
                  {feature.buyerNames.length > 0 && (
                    <div className="text-xs text-[var(--text-ter)] mt-1">{feature.buyerNames.join(', ')}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* What they said — the conversation that shaped results */}
        {purchaseNotes.length > 0 && (
          <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4">
            <h3 className="font-semibold text-[var(--mustard)] mb-3">Why they bought what they bought</h3>
            <div className="space-y-2">
              {purchaseNotes.map((note, i) => (
                <div key={i} className="text-sm">
                  <span className="font-medium text-[var(--mustard)]">{note.by}</span>
                  <span className="text-[var(--mustard)]"> on {String(note.featureName)}: </span>
                  <span className="italic text-[var(--mustard)]">"{String(note.note)}"</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Participant summary */}
        <div className="mt-6">
          <h3 className="font-semibold text-[var(--text-pri)] mb-3">Participants</h3>
          <div className="space-y-2">
            {game.participants.map(p => {
              const config = game.session?.config as { budget_per_participant?: number } | undefined;
              const budget = config?.budget_per_participant ?? 0;
              const spent = budget - p.remainingBudget;
              return (
                <div key={p.name} className="flex items-center justify-between p-3 bg-[var(--surface)] border border-[var(--rule)] text-sm">
                  <span className="font-medium text-[var(--text-pri)]">{p.name}{p.isHost ? ' (host)' : ''}</span>
                  <span className="text-[var(--text-sec)]">${spent.toFixed(0)} spent of ${budget.toFixed(0)}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-2 text-sm text-[var(--text-ter)] text-right">
            Total spent: ${totalSpent.toFixed(0)} across {game.participants.length} participant{game.participants.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Full transcript column */}
      <div className="w-72 shrink-0">
        <h3 className="font-semibold text-[var(--text-pri)] mb-3 flex items-center justify-between">
          Full transcript
          <span className="text-xs text-[var(--text-ter)] font-normal">{game.events.length} events</span>
        </h3>
        <div className="bg-[var(--surface)] border border-[var(--rule)] overflow-y-auto max-h-[600px] px-3 py-2">
          {game.events.map(event => (
            <TranscriptEntry key={event.id} event={event} />
          ))}
        </div>
      </div>
    </div>
  );
}
